import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DollarSign, Clock, AlertTriangle, Shield, FileText, Users, XCircle, RefreshCw, Package } from 'lucide-react';

const STATUS_COLORS = {
  ready_for_patient_responsibility: 'bg-emerald-100 text-emerald-800',
  small_balance_accumulation: 'bg-amber-100 text-amber-800',
  pending_primary: 'bg-slate-100 text-slate-700',
  pending_secondary: 'bg-slate-100 text-slate-700',
  pending_appeal_or_dispute: 'bg-red-100 text-red-800',
  hold_special_circumstance: 'bg-orange-100 text-orange-800',
  resolved: 'bg-gray-100 text-gray-500',
  ready_for_statement_review: 'bg-emerald-100 text-emerald-800',
  approved_for_statement_generation: 'bg-blue-100 text-blue-800',
  manually_deferred: 'bg-amber-100 text-amber-800',
  manually_suppressed: 'bg-gray-100 text-gray-500',
  ready_for_collections_review: 'bg-emerald-100 text-emerald-800',
  approved_for_collections: 'bg-blue-100 text-blue-800',
  ready_for_writeoff_review: 'bg-emerald-100 text-emerald-800',
  approved_for_writeoff_execution: 'bg-blue-100 text-blue-800',
  not_ready_recent_statement: 'bg-slate-100 text-slate-600',
  not_ready_policy_block: 'bg-orange-100 text-orange-700',
  not_applicable: 'bg-gray-100 text-gray-400',
};

function StatusBadge({ status }) {
  if (!status) return null;
  const color = STATUS_COLORS[status] || 'bg-gray-100 text-gray-600';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`} data-testid="status-badge">{status.replace(/_/g, ' ')}</span>;
}

function FlagChips({ item }) {
  const flags = [];
  if (item.manual_hold_active) flags.push({ label: 'Hold', color: 'bg-red-100 text-red-700' });
  if (item.manual_suppress_active) flags.push({ label: 'Suppressed', color: 'bg-gray-200 text-gray-600' });
  if (item.hardship_flag) flags.push({ label: 'Hardship', color: 'bg-orange-100 text-orange-700' });
  if (item.dispute_flag) flags.push({ label: 'Dispute', color: 'bg-red-100 text-red-700' });
  if (item.statement_manual_defer_active) flags.push({ label: 'Stmt Deferred', color: 'bg-amber-100 text-amber-700' });
  if (item.statement_manual_suppress_active) flags.push({ label: 'Stmt Suppressed', color: 'bg-gray-200 text-gray-600' });
  if (item.collections_manual_defer_active) flags.push({ label: 'Coll Deferred', color: 'bg-amber-100 text-amber-700' });
  if (item.collections_manual_suppress_active) flags.push({ label: 'Coll Suppressed', color: 'bg-gray-200 text-gray-600' });
  if (!flags.length) return null;
  return <div className="flex flex-wrap gap-1">{flags.map((f, i) => <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${f.color}`}>{f.label}</span>)}</div>;
}

function formatCents(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function QueueTable({ items, columns, onRowClick, loading, error, onRetry, emptyMessage }) {
  if (loading) return <div className="space-y-2 p-4" data-testid="queue-loading">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>;
  if (error) return <div className="p-8 text-center" data-testid="queue-error"><AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-400" /><p className="text-sm text-muted-foreground mb-2">{error}</p><Button size="sm" variant="outline" onClick={onRetry}><RefreshCw className="h-3 w-3 mr-1" />Retry</Button></div>;
  if (!items?.length) return <div className="p-8 text-center text-muted-foreground" data-testid="queue-empty"><p className="text-sm">{emptyMessage || 'No items in this queue.'}</p></div>;
  return (
    <div className="overflow-x-auto" data-testid="queue-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b bg-muted/30">{columns.map(c => <th key={c.key} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{c.label}</th>)}</tr></thead>
        <tbody>{items.map((item, idx) => (
          <tr key={item.case_id || idx} className="border-b hover:bg-muted/20 cursor-pointer transition-colors" onClick={() => onRowClick?.(item)} data-testid={`queue-row-${idx}`}>
            {columns.map(c => <td key={c.key} className="px-3 py-2.5 whitespace-nowrap">{c.render ? c.render(item) : item[c.key] ?? '—'}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function useQueue(endpoint, defaultSort = 'last_evaluated_at') {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState(defaultSort);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(endpoint, { params: { sort_by: sortBy, sort_order: -1, limit, offset } });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load queue data.');
    } finally {
      setLoading(false);
    }
  }, [endpoint, sortBy, limit, offset]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { items, total, loading, error, sortBy, setSortBy, offset, setOffset, limit, refresh: fetchData };
}

const RESP_COLUMNS = [
  { key: 'patient_id', label: 'Patient', render: i => <span className="font-medium text-xs">{i.patient_id?.slice(0, 12)}...</span> },
  { key: 'current_state', label: 'State', render: i => <StatusBadge status={i.current_state} /> },
  { key: 'balance', label: 'Balance', render: i => <span className="font-mono text-xs">{formatCents(i.balance_amount_cents)}</span> },
  { key: 'aging_days', label: 'Aging', render: i => <span className="text-xs">{i.aging_days}d</span> },
  { key: 'flags', label: 'Flags', render: i => <FlagChips item={i} /> },
  { key: 'last_evaluated_at', label: 'Last Evaluated', render: i => <span className="text-xs text-muted-foreground">{formatDate(i.last_evaluated_at)}</span> },
];

const STMT_COLUMNS = [
  { key: 'patient_id', label: 'Patient', render: i => <span className="font-medium text-xs">{i.patient_id?.slice(0, 12)}...</span> },
  { key: 'statement_readiness_status', label: 'Status', render: i => <StatusBadge status={i.statement_readiness_status} /> },
  { key: 'balance', label: 'Balance', render: i => <span className="font-mono text-xs">{formatCents(i.balance_amount_cents)}</span> },
  { key: 'aging_days', label: 'Aging', render: i => <span className="text-xs">{i.aging_days}d</span> },
  { key: 'flags', label: 'Flags', render: i => <FlagChips item={i} /> },
  { key: 'statement_last_evaluated_at', label: 'Last Evaluated', render: i => <span className="text-xs text-muted-foreground">{formatDate(i.statement_last_evaluated_at)}</span> },
];

const COLL_COLUMNS = [
  { key: 'patient_id', label: 'Patient', render: i => <span className="font-medium text-xs">{i.patient_id?.slice(0, 12)}...</span> },
  { key: 'readiness_status', label: 'Status', render: i => <StatusBadge status={i.readiness_status} /> },
  { key: 'balance', label: 'Balance', render: i => <span className="font-mono text-xs">{formatCents(i.balance_amount_cents)}</span> },
  { key: 'aging_days', label: 'Aging', render: i => <span className="text-xs">{i.aging_days}d</span> },
  { key: 'statements_sent_count', label: 'Stmts Sent', render: i => <span className="text-xs">{i.statements_sent_count ?? 0}</span> },
  { key: 'collections_last_evaluated_at', label: 'Last Evaluated', render: i => <span className="text-xs text-muted-foreground">{formatDate(i.collections_last_evaluated_at)}</span> },
];

const WO_COLUMNS = [
  { key: 'patient_id', label: 'Patient', render: i => <span className="font-medium text-xs">{i.patient_id?.slice(0, 12)}...</span> },
  { key: 'writeoff_readiness_status', label: 'Status', render: i => <StatusBadge status={i.writeoff_readiness_status} /> },
  { key: 'balance', label: 'Balance', render: i => <span className="font-mono text-xs">{formatCents(i.balance_amount_cents)}</span> },
  { key: 'aging_days', label: 'Aging', render: i => <span className="text-xs">{i.aging_days}d</span> },
  { key: 'flags', label: 'Flags', render: i => <FlagChips item={i} /> },
  { key: 'writeoff_last_evaluated_at', label: 'Last Evaluated', render: i => <span className="text-xs text-muted-foreground">{formatDate(i.writeoff_last_evaluated_at)}</span> },
];

function QueuePanel({ endpoint, columns, defaultSort, emptyMessage, onRowClick }) {
  const q = useQueue(endpoint, defaultSort);
  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/10">
        <span className="text-xs text-muted-foreground">{q.total} item{q.total !== 1 ? 's' : ''}</span>
        <div className="flex items-center gap-2">
          <Select value={q.sortBy} onValueChange={q.setSortBy}>
            <SelectTrigger className="h-7 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="last_evaluated_at">Last Evaluated</SelectItem>
              <SelectItem value="balance_amount_cents">Balance</SelectItem>
              <SelectItem value="aging_days">Aging</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={q.refresh}><RefreshCw className="h-3 w-3" /></Button>
        </div>
      </div>
      <QueueTable items={q.items} columns={columns} onRowClick={onRowClick} loading={q.loading} error={q.error} onRetry={q.refresh} emptyMessage={emptyMessage} />
    </div>
  );
}

export default function RevenueWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'responsibility';

  const handleRowClick = (item) => {
    const caseId = item.case_id || item.id;
    if (caseId) navigate(`/revenue/case/${caseId}`);
  };

  const TABS = [
    { value: 'responsibility', label: 'Responsibility', icon: DollarSign },
    { value: 'statements', label: 'Statements', icon: FileText },
    { value: 'collections', label: 'Collections', icon: Users },
    { value: 'writeoff', label: 'Write-Off', icon: XCircle },
  ];

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4" data-testid="revenue-workspace">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Revenue Cycle Workspace</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate('/revenue/batches')} data-testid="goto-batches">
            <Package className="h-3 w-3" />Batches
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate('/revenue/policies')} data-testid="goto-policies">
            <Shield className="h-3 w-3" />Policies
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate('/revenue/analytics')} data-testid="goto-analytics">
            <DollarSign className="h-3 w-3" />Analytics
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={v => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="w-full justify-start bg-muted/30 p-1 h-auto flex-wrap">
          {TABS.map(t => (
            <TabsTrigger key={t.value} value={t.value} className="text-xs gap-1.5 data-[state=active]:bg-background" data-testid={`tab-${t.value}`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <Card className="mt-3 border">
          <TabsContent value="responsibility" className="m-0">
            <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm font-medium flex items-center gap-2"><DollarSign className="h-4 w-4" />Patient Responsibility Queue</CardTitle></CardHeader>
            <CardContent className="p-0">
              <QueuePanel endpoint="/revenue/queues/patient-responsibility" columns={RESP_COLUMNS} defaultSort="last_evaluated_at" emptyMessage="No cases in patient responsibility queue." onRowClick={handleRowClick} />
            </CardContent>
          </TabsContent>

          <TabsContent value="statements" className="m-0">
            <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4" />Statement Readiness Queue</CardTitle></CardHeader>
            <CardContent className="p-0">
              <QueuePanel endpoint="/revenue/queues/statement-readiness" columns={STMT_COLUMNS} defaultSort="statement_last_evaluated_at" emptyMessage="No cases in statement readiness queue." onRowClick={handleRowClick} />
            </CardContent>
          </TabsContent>

          <TabsContent value="collections" className="m-0">
            <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4" />Collections Readiness Queue</CardTitle></CardHeader>
            <CardContent className="p-0">
              <QueuePanel endpoint="/revenue/queues/collections-readiness" columns={COLL_COLUMNS} defaultSort="collections_last_evaluated_at" emptyMessage="No cases in collections readiness queue." onRowClick={handleRowClick} />
            </CardContent>
          </TabsContent>

          <TabsContent value="writeoff" className="m-0">
            <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm font-medium flex items-center gap-2"><XCircle className="h-4 w-4" />Write-Off Readiness Queue</CardTitle></CardHeader>
            <CardContent className="p-0">
              <QueuePanel endpoint="/revenue/queues/writeoff-readiness" columns={WO_COLUMNS} defaultSort="writeoff_last_evaluated_at" emptyMessage="No cases in write-off readiness queue." onRowClick={handleRowClick} />
            </CardContent>
          </TabsContent>
        </Card>
      </Tabs>
    </div>
  );
}
